import React, { useState, Component } from 'react';
import { Card } from '../../SubscriptionAppCard';
import { Input } from '../../SubscriptionAppInput';
import { User, Mail, Phone, DollarSign, TrendingDown, Copy, Check, ExternalLink } from 'lucide-react';
// Inline CopyButton component
function CopyButton({
  onClick,
  copied,
  ariaLabel
}: {
  onClick: () => void;
  copied: boolean;
  ariaLabel: string;
}) {
  return <button onClick={onClick} className="p-1.5 hover:bg-[#F1F5F9] rounded transition-colors" aria-label={ariaLabel} data-id="element-4833" data-mp="3gq0g3a465qep">
      {copied ? <Check className="w-4 h-4 text-[#10B981]" data-id="element-4834" data-mp="3lpd4hzaov5je" /> : <Copy className="w-4 h-4 text-[#64748B]" data-id="element-4835" data-mp="3qnod66p8za01" />}
    </button>;
}
// Inline InfoRow component
function InfoRow({
  icon: Icon,
  value,
  valueColor = '#475569',
  valueFontWeight = 'normal',
  iconColor = '#64748B',
  action
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  value: React.ReactNode;
  valueColor?: string;
  valueFontWeight?: 'normal' | 'medium' | 'semibold';
  iconColor?: string;
  action?: React.ReactNode;
}) {
  const fontWeightClass = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold'
  }[valueFontWeight];
  return <div className="flex items-center justify-between gap-3" data-id="element-4836" data-mp="3m5z2i82wx3vb">
      <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-4837" data-mp="3hc4bskow3omq">
        <Icon className="w-5 h-5 flex-shrink-0" style={{
        color: iconColor
      }} data-id="element-4838" data-mp="3dbu493lg8q5r" />
        <span className={`text-[14px] leading-[1.5] ${fontWeightClass}`} style={{
        color: valueColor
      }} data-id="element-4839" data-mp="38chk4qlypx6v">
          {value}
        </span>
      </div>
      {action && <div className="flex-shrink-0" data-id="element-4840" data-mp="33fhcwcddmoe6">{action}</div>}
    </div>;
}
// Inline Divider component
function Divider() {
  return <div className="border-t border-[#E2E8F0]" data-id="element-4841" data-mp="2yg4ohn6ux99h" />;
}
// Email validation function
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
// International phone validation function
// Accepts international format with country code (e.g., +61 4XX XXX XXX, +1 555-123-4567)
// This is a simplified validation that checks for:
// - Starts with + followed by 1-3 digits (country code)
// - Followed by 6-15 digits (with optional spaces, dashes, or parentheses)
function isValidInternationalPhone(phone: string): boolean {
  // Remove all spaces, dashes, and parentheses for validation
  const cleanPhone = phone.replace(/[\s\-()]/g, '');
  // Check if it starts with + and has valid format
  // Country code (1-3 digits) + subscriber number (6-15 digits)
  const internationalPhoneRegex = /^\+\d{1,3}\d{6,15}$/;
  return internationalPhoneRegex.test(cleanPhone);
}
// Helper to format Australian phone numbers as user types
function formatAustralianPhone(value: string): string {
  // Remove all non-digit characters except +
  const cleaned = value.replace(/[^\d+]/g, '');
  // If it starts with +61 (Australia), format it nicely
  if (cleaned.startsWith('+61')) {
    const digits = cleaned.substring(3); // Remove +61
    if (digits.length <= 1) return '+61 ' + digits;
    if (digits.length <= 4) return '+61 ' + digits.substring(0, 1) + ' ' + digits.substring(1);
    if (digits.length <= 7) return '+61 ' + digits.substring(0, 1) + ' ' + digits.substring(1, 4) + ' ' + digits.substring(4);
    return '+61 ' + digits.substring(0, 1) + ' ' + digits.substring(1, 4) + ' ' + digits.substring(4, 7) + (digits.length > 7 ? ' ' + digits.substring(7, 10) : '');
  }
  return cleaned;
}
interface CustomerSummaryProps {
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  totalPaid: string;
  totalSaved: string;
  isEditing?: boolean;
  onEmailChange?: (email: string) => void;
  onPhoneChange?: (phone: string) => void;
  onViewInShopify?: () => void;
  onViewContact?: () => void;
  onSendMagicLink?: () => void;
  isSendingMagicLink?: boolean;
  onOpenAsCustomer?: () => void;
  'data-id'?: string;
}
export function CustomerSummary({
  customerName,
  customerEmail,
  customerPhone = '+61 4XX XXX XXX',
  totalPaid,
  totalSaved,
  isEditing = false,
  onEmailChange,
  onPhoneChange,
  onViewInShopify,
  onViewContact,
  onSendMagicLink,
  isSendingMagicLink = false,
  onOpenAsCustomer,
  'data-id': dataId
}: CustomerSummaryProps) {
  const [emailCopied, setEmailCopied] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);
  const [emailError, setEmailError] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(customerEmail);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };
  const handleCopyPhone = async () => {
    try {
      await navigator.clipboard.writeText(customerPhone);
      setPhoneCopied(true);
      setTimeout(() => setPhoneCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy phone:', err);
    }
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    onEmailChange?.(newEmail);
    // Validate email
    if (newEmail && !isValidEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhone = e.target.value;
    // Format if it looks like an Australian number
    const formatted = formatAustralianPhone(newPhone);
    onPhoneChange?.(formatted);
    // Validate phone
    if (newPhone && !isValidInternationalPhone(newPhone)) {
      setPhoneError('Please enter a valid phone number with country code (e.g., +61 4XX XXX XXX)');
    } else {
      setPhoneError('');
    }
  };
  return <Card padding="none" data-id="element-4842" data-mp="1370d56prnkoz">
      <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white flex items-center justify-between gap-3" data-id="element-4843" data-mp="137ilh1ppdpd9">
        <h3 className="text-[20px] font-semibold text-[#0F172A] leading-[1.4]" data-id="element-4844" data-mp="y9h20fod7o19">
          Customer
        </h3>
        {onViewInShopify && <button type="button" onClick={onViewInShopify} className="inline-flex items-center gap-1 text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-4845" data-mp="14ne9gbh0vdys">
            View in Shopify <ExternalLink className="w-3.5 h-3.5" data-id="element-4846" data-mp="up6shsmqgvws" />
          </button>}
      </div>

      <div className="p-6 space-y-5" data-id="element-4847" data-mp="urb2msb86r5i">
        {/* Customer Name */}
        <InfoRow icon={User} value={customerName} valueColor="#0F172A" valueFontWeight="medium" data-id="element-4848" data-mp="gwh1taw9f2tc" />

        {/* Customer Email */}
        {isEditing ? <div className="flex items-start gap-3" data-id="element-4849" data-mp="705pwm0l0ezf">
            <Mail className="w-5 h-5 flex-shrink-0 text-[#64748B] mt-2.5" data-id="element-4850" data-mp="71gw0shi1ytp" />
            <div className="flex-1" data-id="element-4851" data-mp="c00icu009cml">
              <Input type="email" value={customerEmail} onChange={handleEmailChange} placeholder="customer@example.com" error={emailError} data-id="element-4852" data-mp="bww5774kb6js" />
            </div>
          </div> : <InfoRow icon={Mail} value={<span className="break-all" data-id="element-4854" data-mp="2xhf48sb4xzt">{customerEmail}</span>} action={<CopyButton onClick={handleCopyEmail} copied={emailCopied} ariaLabel="Copy email address" data-id="element-4855" data-mp="3fh3f3ynf3din" />} data-id="element-4853" data-mp="2x930j40dwpo" />}

        {/* Customer Phone */}
        {isEditing ? <div className="flex items-start gap-3" data-id="element-4856" data-mp="3akwjozsndkey">
            <Phone className="w-5 h-5 flex-shrink-0 text-[#64748B] mt-2.5" data-id="element-4857" data-mp="3oitdjofpvcpx" />
            <div className="flex-1" data-id="element-4858" data-mp="3kh7uflcla27a">
              <Input type="tel" value={customerPhone} onChange={handlePhoneChange} placeholder="+61 4XX XXX XXX" error={phoneError} data-id="element-4859" data-mp="3kg6m3omyjtt9" />
            </div>
          </div> : <InfoRow icon={Phone} value={customerPhone} action={<CopyButton onClick={handleCopyPhone} copied={phoneCopied} ariaLabel="Copy phone number" data-id="element-4861" data-mp="3th4o61du5r97" />} data-id="element-4860" data-mp="3thxmcdyqb8fq" />}

        <Divider data-id="element-4862" data-mp="c0d2h0xby27z" />

        {/* Total Paid */}
        <div className="flex items-center justify-between" data-id="element-4863" data-mp="71jo76a7y76r">
          <InfoRow icon={DollarSign} value="Total paid" data-id="element-4864" data-mp="7466o0yne73r" />
          <span className="text-[14px] font-semibold text-[#0F172A] leading-[1.5]" data-id="element-4865" data-mp="l2w4xjx7ectp">
            {totalPaid}
          </span>
        </div>

        {/* Total Saved */}
        <div className="flex items-center justify-between" data-id="element-4866" data-mp="1nfh0xprx1uu5">
          <InfoRow icon={TrendingDown} iconColor="#10B981" value="Total saved" data-id="element-4867" data-mp="1nexim7yct4fl" />
          <span className="text-[14px] font-semibold text-[#10B981] leading-[1.5]" data-id="element-4868" data-mp="1scyykk0hgnmu">
            {totalSaved}
          </span>
        </div>

        {(onViewContact || onSendMagicLink || onOpenAsCustomer) && <>
            <Divider data-id="element-4869" data-mp="21egfu5pmmvd4" />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-4870" data-mp="21cm49i08wflp">
              {onViewContact && <button type="button" onClick={onViewContact} className="text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-4871" data-mp="14mmlyvuam2px">
                  View contact details
                </button>}
              {onSendMagicLink && <button type="button" onClick={onSendMagicLink} disabled={isSendingMagicLink} className="text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7] disabled:text-[#94A3B8] transition-colors" data-id="element-4872" data-mp="1ignnfxcmq3tz">
                  {isSendingMagicLink ? 'Sending…' : 'Send magic link'}
                </button>}
              {onOpenAsCustomer && <button type="button" onClick={onOpenAsCustomer} className="text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-4873" data-mp="iez9qrodefkb">
                  Open as customer
                </button>}
            </div>
          </>}
      </div>
    </Card>;
}
export type { CustomerSummaryProps };